// Steering a drafted lens: turn the judge's field NAMES into field PATHS by reading
// the raw instances.
//
// This is the SOP's induce step, scoped to one decision. The judge names the slots
// worth extracting and cannot say where they live, because a path is a fact about
// one harness's argument names rather than about the behaviour. A draft therefore
// binds only the statement and the program — which is exactly the thin reading a
// first mount produces: a group per program, a timestamp per row, nothing a reader
// could act on.
//
// A model CAN answer this, and it is the right shape of question for one: it is
// located entirely in evidence the model is shown, the answer is checkable against
// the same whitelist a hand-written spec faces, and a wrong answer is visible
// immediately as an empty column rather than as a plausible-looking value.
//
// What it must not become is a classifier for meaning. It is not asked whether a
// behaviour is interesting, nor what the fields mean — the judge already decided
// both. It is asked only: given these commands, where in them does `source_file`
// live.

import { validateRuntimeSpec } from "./xrayRuntimeLens.js";
import { redactPaths } from "../../xray-factory/src/mine/skeleton.js";

export const REFINE_SCHEMA = {
  type: "object",
  required: ["fields", "groupBy"],
  properties: {
    fields: {
      type: "object",
      description:
        "Field name → path. One entry per field you can actually locate in the evidence. "
        + "OMIT a field you cannot locate — a wrong path is worse than an absent one, because it "
        + "renders as the tool not carrying the value.",
      additionalProperties: { type: "string" },
    },
    groupBy: {
      type: "string",
      description:
        "Which of your fields is the UNIT a reader sees — the thing they would say this row is "
        + "about. Prefer the target the work acts on (a file, a namespace, a table) over the "
        + "program that did it: grouping by the program collapses every row into one.",
    },
    within: {
      type: "string",
      description:
        "OPTIONAL second identity, nested inside groupBy — the relationship a reader would draw "
        + "as `container → item`: host → page, database → table, namespace → service. Give this "
        + "ONLY when both levels are real and the outer one would be useless alone; a nesting "
        + "invented to look structured renders as every row containing exactly one row.",
    },
    compare: {
      type: "string",
      description:
        "OPTIONAL field to track ACROSS repeat calls to the same groupBy value, so the lens can "
        + "say whether it changed between sightings. Almost always the output or a result field. "
        + "A target that changed under the agent is the case where its conclusion may not hold, "
        + "so this is high value whenever the same thing is touched twice.",
    },
    unlocatable: {
      type: "array",
      description: "Field names you were asked for and could not find, so a reader knows they were not forgotten.",
      items: { type: "string" },
    },
    notes: { type: "string", description: "One sentence on anything a reader of this lens should distrust." },
  },
};

export const REFINE_INSTRUCTIONS = `You are locating values inside agent commands so a lens can extract them.

You are given field NAMES someone already decided are worth extracting, and several
RAW instances of the work. Your only job is to say where in the evidence each field
lives, using the path vocabulary below. You are not deciding whether the fields
matter — that is settled.

PATH VOCABULARY, and nothing outside it:

  command                 the whole statement, verbatim
  program                 the resolved program name
  output                  what came back
  outcome                 answered / unrecorded / backgrounded / shell-error
  flag:-n | flag:--name   the VALUE of that flag
  arg:0 | arg:1 | …       the Nth positional, after flags-with-values are removed
  input.<key>[.<key>]     a structured tool call's argument
  result.text             a structured tool call's result text
  capture:<regex>         first capture group of <regex> against the command
  outputCapture:<regex>   first capture group of <regex> against the output

RULES, each of which exists because its absence produces a lens that looks
populated and is wrong:

1. OMIT WHAT YOU CANNOT LOCATE, and list it under \`unlocatable\`. A guessed path
   binds nothing and renders as the tool not carrying the value, which reads as a
   gap in the data rather than a gap in the lens.

2. PREFER A NAMED SOURCE OVER A CAPTURE. \`flag:-n\` survives a reordered command;
   a regex over the whole statement does not. Use a capture only when the value
   genuinely lives inside free text — an embedded \`python -c\` payload, a URL, a
   quoted path.

3. A CAPTURE MUST BE ANCHORED ON SOMETHING STRUCTURAL. Anchor on the API call or
   the literal that surrounds the value — \`Path\\\\(['"]([^'"]+)\` — never on
   position alone. A pattern that happens to match the sample will not match the
   next instance.

4. DO NOT CAPTURE A CREDENTIAL. If a field name asks for a token, key, password or
   secret, put it in \`unlocatable\` and say so in \`notes\`. A lens that audits
   access to secrets must not become the easiest place to read them.

5. GROUP BY THE TARGET, NOT THE TOOL. The unit a reader sees should be the thing
   acted on. Every instance you are shown probably ran the same program, so
   grouping by program yields exactly one row.

Read every instance before answering. A path that fits the first one and not the
rest is the failure this step exists to prevent.`;

/**
 * The payload a model answers to locate a drafted lens's fields.
 *
 * Evidence is RAW and redacted, aggregates are absent: there is nothing to
 * summarize here, and a count would tell the model nothing about where a value sits.
 */
export function buildRefineRequest(spec, cluster, { wanted = [], instances = 8, redact = true } = {}) {
  const asked = wanted.length ? wanted : Object.keys(spec?.fields || {});
  const raw = (cluster?.instances || []).slice(0, instances)
    .map((inst, i) => `  [${i + 1}] ${redact ? redactPaths(String(inst?.command || "")) : String(inst?.command || "")}`)
    .join("\n");

  const door = spec?.match?.shell
    ? `shell commands (programs: ${(spec.match.shell.programs || []).join(", ") || "by pattern"})`
    : `structured tool calls (${(spec?.match?.tools || []).join(", ") || "by pattern"})`;

  return {
    instructions: REFINE_INSTRUCTIONS,
    schema: REFINE_SCHEMA,
    prompt:
      `Lens: ${spec?.label} — ${spec?.title}\n`
      + `Read by: ${spec?.consumer}\n`
      + `Door: ${door}\n\n`
      + `Fields to locate: ${asked.join(", ")}\n\n`
      + `Raw instances (this is the evidence):\n${raw}\n`,
    asked,
  };
}

/**
 * Check a refinement before it is applied.
 *
 * Runs the candidate through the SPEC validator rather than a private set of rules,
 * so a path the compiler would reject cannot be stored — the two must not disagree
 * about what a legal path is.
 */
export function validateRefinement(response, spec, { asked = [] } = {}) {
  const problems = [];
  const fields = response?.fields && typeof response.fields === "object" ? response.fields : null;
  if (!fields || !Object.keys(fields).length) {
    return { ok: false, problems: ["no fields located"], spec: null };
  }

  const groupBy = response?.groupBy;
  if (!groupBy || !Object.prototype.hasOwnProperty.call(fields, groupBy)) {
    problems.push("groupBy must name one of the located fields");
  }

  // Keep the draft's own DERIVABLE fields alongside the located ones. A refinement
  // that replaced `fields` wholesale dropped `output` — which silently removed the
  // result rendering AND the sighting comparison that depends on it, turning a
  // richer lens into a poorer one. `command` matters for the same reason at the
  // other end: it is the evidence a reader falls back to when a located field comes
  // back empty, so without it a mis-located path is unfalsifiable from the panel.
  //
  // Only the always-available sources are carried, never a capture: a draft never
  // has one, and a stale capture from an older refinement should be replaced rather
  // than merged under.
  const DERIVABLE = new Set(["command", "program", "output", "outcome"]);
  const carried = Object.fromEntries(
    Object.entries(spec?.fields || {}).filter(([, path]) => DERIVABLE.has(path)),
  );
  const merged = { command: "command", ...carried, ...fields };
  const within = typeof response?.within === "string" && response.within !== groupBy
    && Object.prototype.hasOwnProperty.call(merged, response.within)
    ? response.within
    : null;
  const compare = typeof response?.compare === "string"
    && Object.prototype.hasOwnProperty.call(merged, response.compare)
    ? response.compare
    : spec.sighting?.compare || null;

  const candidate = {
    ...spec,
    fields: merged,
    group: { by: groupBy, label: `{${groupBy}}`, ...(within ? { within, innerLabel: `{${within}}` } : {}) },
    // A nesting makes it a tree; without one it stays the stream it was. Declared
    // explicitly so the two never disagree — the validator refuses a tree with no
    // item level.
    shape: within ? "tree" : (spec.shape || "stream"),
    ...(compare ? { sighting: { compare } } : {}),
  };
  const shape = validateRuntimeSpec(candidate);
  if (!shape.ok) problems.push(...shape.problems);

  const unlocatable = Array.isArray(response?.unlocatable) ? response.unlocatable : [];
  const missed = asked.filter((f) => !Object.prototype.hasOwnProperty.call(fields, f) && !unlocatable.includes(f));

  return {
    ok: problems.length === 0,
    problems,
    spec: problems.length === 0 ? candidate : null,
    // Reported rather than treated as failure: a field nobody could locate is a real
    // outcome, and it belongs on screen so a reader knows the column is absent by
    // decision rather than by accident.
    unlocatable,
    silentlyDropped: missed,
    notes: typeof response?.notes === "string" ? response.notes : "",
  };
}
